import { REPORT_MODULES, type ModuleCode, type ReportKind } from '@horevia/contracts';
import type { ReportGroupBy, ReportScope } from '@/features/analytics/queries';

/**
 * Catalogue des 18 rapports (FR-DSH-06, SCR-100 à SCR-104 ; `EXPENSES` depuis
 * le sprint 2.9, D-052 ; `SALES`, `CONSOLIDATED_REVENUE`, `PRODUCT_MARGIN` et
 * `STOCK_CONSUMPTION` depuis Horevia Analytics, ADR-024 ; `CUSTOMER_ACTIVITY` et
 * `LOYALTY` depuis Horevia CRM, ADR-025).
 *
 * Un rapport peut exiger un **module** (R-ANA-04) : les rapports hôteliers
 * n'apparaissent qu'avec `hotel`, ceux de l'analyse transverse avec
 * `analytics` ; le serveur répond 404 `MODULE_NOT_ENABLED` sinon.
 *
 * Chaque entrée décrit la famille d'accueil, la permission requise, les
 * regroupements proposés et le filtre de statut pertinent. Le serveur reste
 * l'autorité : une famille masquée ici répond de toute façon 403.
 *
 * Règles d'autorisation, copiées de `GET /reports/:kind`
 * (`apps/api/src/modules/analytics`) :
 *   - `report.operational` ouvre la route et suffit aux rapports
 *     d'exploitation (`OCCUPANCY`, `RESERVATIONS`) ;
 *   - `report.financial` est exigé **en plus** pour les neuf autres ;
 *   - `report.export` est exigé en plus pour `format=csv`.
 *
 * Les regroupements listés sont exactement ceux que le serveur honore : un
 * `groupBy` inconnu n'est pas rejeté par l'API mais retombe sur le défaut, et un
 * menu qui ne change rien tromperait l'utilisateur. De même, seul le rapport
 * `RESERVATIONS` applique le filtre `status`.
 *
 * Hypothèse **A-050** (à confirmer par Bertrand) : les rapports « Clients » et
 * « Entreprises » exposent le chiffre d'affaires par client et relèvent donc de
 * `report.financial`, et non de `report.operational`.
 */

export const REPORT_FAMILIES = ['ACTIVITY', 'SALES', 'FINANCE', 'CUSTOMERS', 'RESOURCES'] as const;
export type ReportFamily = (typeof REPORT_FAMILIES)[number];

/** Permission qui ouvre la route `GET /reports/:kind`, quel que soit le rapport. */
export const REPORTS_ENTRY_PERMISSION = 'report.operational';

export interface ReportDefinition {
  kind: ReportKind;
  family: ReportFamily;
  /**
   * Permission propre au rapport. `report.operational` est de toute façon
   * requise pour ouvrir la route (voir `canOpenReport`).
   */
  permission: 'report.operational' | 'report.financial';
  /** Regroupements honorés par le serveur ; le premier est la valeur par défaut. Vide = aucun. */
  groupBy: ReportGroupBy[];
  /** Valeurs du filtre de statut (vide = le serveur n'applique pas ce filtre). */
  statuses: string[];
  /** Espace de noms i18n des libellés de statut. */
  statusNamespace?: 'reservations' | 'billing' | 'payments';
  /**
   * Périmètres honorés (rapport `RESERVATIONS`, D-053) ; le premier est la
   * valeur par défaut. Vide = le serveur n'applique pas ce paramètre.
   */
  scopes?: ReportScope[];
  /** Affiche une bande de KPI en tête (totaux monétaires). */
  showTotalsHeader: boolean;
  /**
   * Module requis (ADR-024, R-ANA-04) : `hotel` pour les rapports hôteliers,
   * `analytics` pour l'analyse transverse ; absent = commun à tous les tenants.
   */
  module?: ModuleCode;
}

const moduleOf = (kind: ReportKind): { module?: ModuleCode } =>
  REPORT_MODULES[kind] ? { module: REPORT_MODULES[kind] } : {};

export const REPORTS: ReportDefinition[] = [
  {
    kind: 'RESERVATIONS',
    family: 'ACTIVITY',
    permission: 'report.operational',
    ...moduleOf('RESERVATIONS'),
    groupBy: [],
    statuses: [
      'PENDING_CONFIRMATION',
      'OPTION',
      'CONFIRMED',
      'CHECKED_IN',
      'CHECKED_OUT',
      'CANCELLED',
      'NO_SHOW',
    ],
    statusNamespace: 'reservations',
    scopes: ['ENGAGED', 'ALL', 'CANCELLED'],
    showTotalsHeader: true,
  },
  {
    kind: 'OCCUPANCY',
    family: 'ACTIVITY',
    permission: 'report.operational',
    ...moduleOf('OCCUPANCY'),
    groupBy: ['DAY', 'RESOURCE', 'RESOURCE_TYPE'],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'REVENUE',
    family: 'FINANCE',
    permission: 'report.financial',
    ...moduleOf('REVENUE'),
    groupBy: ['DAY', 'WEEK', 'MONTH', 'RESOURCE', 'SOURCE'],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'PAYMENTS',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: ['METHOD', 'DAY', 'WEEK', 'MONTH'],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'RECEIVABLES',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'DISCOUNTS',
    family: 'FINANCE',
    permission: 'report.financial',
    ...moduleOf('DISCOUNTS'),
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'TAXES',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'CASH',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'EXPENSES',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: ['DAY', 'WEEK', 'MONTH', 'METHOD', 'CATEGORY'],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'CUSTOMERS',
    family: 'CUSTOMERS',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'COMPANIES',
    family: 'CUSTOMERS',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  // Horevia CRM (ADR-025, D-H69) — famille « Clients », module `crm` ; les
  // montants de `CUSTOMER_ACTIVITY` disparaissent sans `report.financial`.
  {
    kind: 'CUSTOMER_ACTIVITY',
    family: 'CUSTOMERS',
    permission: 'report.operational',
    ...moduleOf('CUSTOMER_ACTIVITY'),
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'LOYALTY',
    family: 'CUSTOMERS',
    permission: 'report.operational',
    ...moduleOf('LOYALTY'),
    groupBy: ['DAY', 'WEEK', 'MONTH', 'CUSTOMER'],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'RESOURCE_PERFORMANCE',
    family: 'RESOURCES',
    permission: 'report.financial',
    ...moduleOf('RESOURCE_PERFORMANCE'),
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  // Horevia Analytics (ADR-024, D-H59) — famille « Ventes & stocks », module `analytics`.
  {
    kind: 'SALES',
    family: 'SALES',
    permission: 'report.operational',
    ...moduleOf('SALES'),
    groupBy: [
      'OUTLET',
      'CATEGORY',
      'PRODUCT',
      'HOUR',
      'WEEKDAY',
      'SERVER',
      'SERVICE_TYPE',
      'DAY',
      'WEEK',
      'MONTH',
    ],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'CONSOLIDATED_REVENUE',
    family: 'SALES',
    permission: 'report.financial',
    ...moduleOf('CONSOLIDATED_REVENUE'),
    groupBy: ['MODULE', 'DAY', 'WEEK', 'MONTH'],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'PRODUCT_MARGIN',
    family: 'SALES',
    permission: 'report.financial',
    ...moduleOf('PRODUCT_MARGIN'),
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'STOCK_CONSUMPTION',
    family: 'SALES',
    permission: 'report.financial',
    ...moduleOf('STOCK_CONSUMPTION'),
    groupBy: ['ITEM', 'CATEGORY', 'DAY', 'WEEK', 'MONTH'],
    statuses: [],
    showTotalsHeader: true,
  },
];

const BY_SLUG = new Map(REPORTS.map((report) => [report.kind.toLowerCase(), report]));

/** `revenue`, `resource_performance` → définition du rapport, ou `undefined`. */
export function reportBySlug(slug: string): ReportDefinition | undefined {
  return BY_SLUG.get(slug.toLowerCase());
}

/**
 * Vrai si l'utilisateur peut ouvrir ce rapport : la permission d'entrée de la
 * route **et** celle du rapport (identiques pour un rapport d'exploitation).
 */
export function canOpenReport(
  report: ReportDefinition,
  can: (permission: string) => boolean,
  hasModule: (module: ModuleCode) => boolean = () => true,
): boolean {
  return (
    can(REPORTS_ENTRY_PERMISSION) &&
    can(report.permission) &&
    (!report.module || hasModule(report.module))
  );
}

/** Rapports d'une famille, visibles pour l'utilisateur courant. */
export function reportsOfFamily(
  family: ReportFamily,
  can: (permission: string) => boolean,
  hasModule: (module: ModuleCode) => boolean = () => true,
): ReportDefinition[] {
  return REPORTS.filter(
    (report) => report.family === family && canOpenReport(report, can, hasModule),
  );
}
